import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ImageIcon from "@mui/icons-material/Image";
import CodeIcon from "@mui/icons-material/Code";
import EmailImageInsertMenu from "./EmailImageInsertMenu";
import {
  attachEmailImageClickHandlers,
  defaultNotificationImageUploadHandler,
  insertImageIntoEditor,
  sanitizeEmailNotificationHtml,
} from "./emailEditorUtils";
import "./emailNotificationEditor.css";

const preventToolbarBlur = (event) => event.preventDefault();

const ensureEditorHasBlock = (editorEl) => {
  if (!editorEl) return;

  const html = editorEl.innerHTML.replace(/\u00a0/g, " ").trim();
  if (!html || html === "<br>") {
    editorEl.innerHTML = "<p><br></p>";
    const paragraph = editorEl.querySelector("p");
    if (!paragraph) return;

    const range = document.createRange();
    range.setStart(paragraph, 0);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
};

const DEFAULT_MIN_HEIGHT = 96;
const DEFAULT_MAX_HEIGHT = 240;

const escapeTextForHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");

const sourceToEditorHtml = (raw = "") => {
  const trimmed = String(raw ?? "");
  if (!trimmed.trim()) return "<p><br></p>";
  const normalized = sanitizeEmailNotificationHtml(trimmed);
  if (normalized.trim()) return normalized;
  return `<p>${escapeTextForHtml(trimmed)}</p>`;
};

const EmailRichTextEditor = ({
  value = "",
  onChange,
  onEditorReady,
  onImageUploadError,
  imageUploadHandler = defaultNotificationImageUploadHandler,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  placeholder = "Enter email body",
  maxLength,
}) => {
  const editorRef = useRef(null);
  const sourceRef = useRef(null);
  const imageMenuRef = useRef(null);
  const isFocusedRef = useRef(false);
  const isNormalizingDomRef = useRef(false);
  const skipParentSyncRef = useRef(false);
  const [showSource, setShowSource] = useState(false);
  const [sourceHtml, setSourceHtml] = useState("");
  const [contentHeight, setContentHeight] = useState(minHeight);
  const [isScrollable, setIsScrollable] = useState(false);

  const adjustContentHeight = useCallback(() => {
    const activeEl = showSource ? sourceRef.current : editorRef.current;
    if (!activeEl) return;

    activeEl.style.height = "auto";
    const measuredHeight = activeEl.scrollHeight;
    const nextHeight = Math.min(Math.max(measuredHeight, minHeight), maxHeight);

    setContentHeight(nextHeight);
    setIsScrollable(measuredHeight > maxHeight);
  }, [maxHeight, minHeight, showSource]);

  const emitChange = useCallback(
    ({ normalizeDom = false } = {}) => {
      if (!editorRef.current || typeof onChange !== "function" || showSource) return;

      const normalized = sanitizeEmailNotificationHtml(editorRef.current.innerHTML);
      if (maxLength != null && normalized.length > maxLength) {
        isNormalizingDomRef.current = true;
        editorRef.current.innerHTML = value || "<p><br></p>";
        isNormalizingDomRef.current = false;
        return;
      }
      if (normalizeDom && normalized !== editorRef.current.innerHTML) {
        isNormalizingDomRef.current = true;
        editorRef.current.innerHTML = normalized;
        isNormalizingDomRef.current = false;
      }
      onChange(normalized);
      requestAnimationFrame(adjustContentHeight);
    },
    [adjustContentHeight, maxLength, onChange, showSource, value],
  );

  const applyExecCommand = useCallback(
    (command, commandValue) => {
      if (showSource) return;
      const editorEl = editorRef.current;
      if (!editorEl) return;

      editorEl.focus();
      if (
        command === "bold"
        || command === "italic"
        || command === "strikeThrough"
        || command === "removeFormat"
        || command === "insertOrderedList"
        || command === "insertUnorderedList"
      ) {
        ensureEditorHasBlock(editorEl);
      }

      document.execCommand(command, false, commandValue ?? null);
      emitChange({ normalizeDom: true });
    },
    [emitChange, showSource],
  );

  const handleInsertImage = useCallback(
    (url, displayName) => {
      const editorEl = editorRef.current;
      if (!editorEl || showSource) return;
      // File picker blurs the editor; keep sync from overwriting with stale `value`.
      isFocusedRef.current = true;
      editorEl.focus();
      insertImageIntoEditor(editorEl, url, displayName, () =>
        emitChange({ normalizeDom: true })
      );
    },
    [emitChange, showSource],
  );

  const toggleSourceMode = useCallback(() => {
    if (showSource) {
      const nextHtml = sourceToEditorHtml(sourceHtml).slice(
        0,
        maxLength != null ? maxLength : undefined
      );
      skipParentSyncRef.current = true;
      if (editorRef.current) {
        editorRef.current.innerHTML = nextHtml;
      }
      setShowSource(false);
      onChange?.(nextHtml);
      requestAnimationFrame(adjustContentHeight);
      return;
    }

    const currentHtml = editorRef.current?.innerHTML || value || "";
    setSourceHtml(sanitizeEmailNotificationHtml(currentHtml) || currentHtml);
    setShowSource(true);
    requestAnimationFrame(adjustContentHeight);
  }, [adjustContentHeight, maxLength, onChange, showSource, sourceHtml, value]);

  const handleSourceChange = useCallback(
    (event) => {
      const nextHtml =
        maxLength != null
          ? event.target.value.slice(0, maxLength)
          : event.target.value;
      setSourceHtml(nextHtml);
      onChange?.(sourceToEditorHtml(nextHtml).slice(0, maxLength != null ? maxLength : undefined));
      requestAnimationFrame(adjustContentHeight);
    },
    [adjustContentHeight, maxLength, onChange],
  );

  const handleInput = useCallback(() => {
    if (isNormalizingDomRef.current) return;
    emitChange({ normalizeDom: false });
  }, [emitChange]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    emitChange({ normalizeDom: true });
  }, [emitChange]);

  useEffect(() => {
    if (showSource) return undefined;

    const editorEl = editorRef.current;
    if (!editorEl) return undefined;

    if (!value?.trim() && !editorEl.innerHTML.trim()) {
      editorEl.innerHTML = "<p><br></p>";
    }

    attachEmailImageClickHandlers(editorEl, () => emitChange({ normalizeDom: true }));
    if (typeof onEditorReady === "function") {
      onEditorReady(editorEl);
    }

    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch (_) { /* noop */ }

    return undefined;
  }, [emitChange, onEditorReady, showSource]);

  useEffect(() => {
    if (!editorRef.current || isFocusedRef.current || showSource) return;
    if (skipParentSyncRef.current) {
      skipParentSyncRef.current = false;
      return;
    }
    const nextValue = value || "";
    const currentHtml = editorRef.current.innerHTML.replace(/\u00a0/g, " ").trim();
    const normalizedNext = nextValue || "<p><br></p>";
    const normalizedCurrent = currentHtml || "<p><br></p>";

    if (normalizedCurrent === normalizedNext) return;

    // Avoid wiping a just-inserted image when parent `value` is still one render behind.
    const currentHasThumb = Boolean(
      editorRef.current.querySelector("img.notification-email-image-thumb")
    );
    const nextHasThumb =
      /notification-email-image-thumb/i.test(normalizedNext)
      || /<img\b/i.test(normalizedNext);
    if (currentHasThumb && !nextHasThumb) return;

    editorRef.current.innerHTML = normalizedNext;
  }, [showSource, value]);

  useLayoutEffect(() => {
    adjustContentHeight();
  }, [adjustContentHeight, showSource, sourceHtml, value]);

  const contentAreaStyle = {
    minHeight,
    height: contentHeight,
    maxHeight,
    overflowY: isScrollable ? "auto" : "hidden",
  };

  return (
    <div className="email-notification-editor">
      <EmailImageInsertMenu
        ref={imageMenuRef}
        onInsertImage={handleInsertImage}
        imageUploadHandler={imageUploadHandler}
        onError={onImageUploadError}
      />

      <div className="email-rt-toolbar">
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("undo")}
          title="Undo"
          aria-label="Undo"
          disabled={showSource}
        >
          <UndoIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("redo")}
          title="Redo"
          aria-label="Redo"
          disabled={showSource}
        >
          <RedoIcon fontSize="small" />
        </IconButton>

        <div className="email-rt-toolbar-divider" />

        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("bold")}
          title="Bold"
          aria-label="Bold"
          disabled={showSource}
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("italic")}
          title="Italic"
          aria-label="Italic"
          disabled={showSource}
        >
          <FormatItalicIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("strikeThrough")}
          title="Strikethrough"
          aria-label="Strikethrough"
          disabled={showSource}
        >
          <FormatStrikethroughIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("removeFormat")}
          title="Remove format"
          aria-label="Remove format"
          disabled={showSource}
        >
          <FormatClearIcon fontSize="small" />
        </IconButton>

        <div className="email-rt-toolbar-divider" />

        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("insertOrderedList")}
          title="Numbered list"
          aria-label="Numbered list"
          disabled={showSource}
        >
          <FormatListNumberedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => applyExecCommand("insertUnorderedList")}
          title="Bulleted list"
          aria-label="Bulleted list"
          disabled={showSource}
        >
          <FormatListBulletedIcon fontSize="small" />
        </IconButton>

        <div className="email-rt-toolbar-divider" />

        <IconButton
          size="small"
          onMouseDown={preventToolbarBlur}
          onClick={() => imageMenuRef.current?.openMenu()}
          title="Insert image"
          aria-label="Insert image"
          disabled={showSource}
        >
          <ImageIcon fontSize="small" />
        </IconButton>

        <div className="email-rt-toolbar-divider" />

        <Button
          size="small"
          variant={showSource ? "contained" : "text"}
          onMouseDown={preventToolbarBlur}
          onClick={toggleSourceMode}
          startIcon={<CodeIcon fontSize="small" />}
          className="email-rt-source-btn"
          aria-pressed={showSource}
          aria-label="Source"
        >
          Source
        </Button>
      </div>

      <textarea
        ref={sourceRef}
        className="email-rt-source"
        value={sourceHtml}
        onChange={handleSourceChange}
        style={{
          ...contentAreaStyle,
          display: showSource ? "block" : "none",
        }}
        spellCheck={false}
        aria-label="Email HTML source"
        hidden={!showSource}
      />
      <div
        ref={editorRef}
        className="email-rt-editor"
        contentEditable={!showSource}
        suppressContentEditableWarning
        style={{
          ...contentAreaStyle,
          display: showSource ? "none" : "block",
        }}
        data-placeholder={placeholder}
        aria-hidden={showSource}
        onFocus={() => {
          isFocusedRef.current = true;
          try {
            document.execCommand("defaultParagraphSeparator", false, "p");
          } catch (_) { /* noop */ }
        }}
        onInput={handleInput}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default EmailRichTextEditor;
